import { useCallback } from 'react';
import { ActionIcon, Badge, Group, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconHandStop, IconMicrophoneOff, IconPin, IconPinnedOff, IconScreenShare } from '@tabler/icons-react';
import {
    isTrackReference,
    type TrackReferenceOrPlaceholder,
    useEnsureTrackRef,
    useFeatureContext,
    useIsMuted,
    useIsSpeaking,
    useMaybeLayoutContext,
    useParticipantInfo,
    VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { useIsRecordingLocally } from '../local-recording';
import { useIsHandRaised } from '../reactions';
import { RecordingDot } from '../recording-indicator';
import { participantName } from './participant-name';
import { RemoveGuestMenu } from './remove-guest-menu';
import classes from './participant-tile.module.css';
import { isSameTrackRef } from './track-refs';
import { UserAvatar } from '@/shared/user-avatar';

export function ParticipantTile({
    trackRef,
    actions = true,
}: Readonly<{ trackRef?: TrackReferenceOrPlaceholder; actions?: boolean }>) {
    const { t } = useTranslation('conference');
    const trackReference = useEnsureTrackRef(trackRef);
    const { participant, source } = trackReference;
    const { name, identity } = useParticipantInfo({ participant });
    const displayName = participantName(name, identity);
    const isSpeaking = useIsSpeaking(participant);
    const handRaised = useIsHandRaised(participant);
    const micMuted = useIsMuted({ participant, source: Track.Source.Microphone });
    const videoMuted = useIsMuted(trackReference);
    const autoManageSubscription = useFeatureContext()?.autoSubscription;
    const layoutContext = useMaybeLayoutContext();
    const pinned = layoutContext?.pin.state?.some((pinnedRef) => isSameTrackRef(pinnedRef, trackReference)) ?? false;
    const isScreenShare = source === Track.Source.ScreenShare;
    const recordingLocally = useIsRecordingLocally(participant);
    const showHand = handRaised && !isScreenShare;
    const showLocalRecording = recordingLocally && !isScreenShare;

    const clearPinOnUnsubscribe = useCallback(
        (subscribed: boolean) => {
            if (!subscribed && pinned && !isScreenShare) layoutContext?.pin.dispatch?.({ msg: 'clear_pin' });
        },
        [layoutContext, pinned, isScreenShare]
    );

    const togglePin = () => {
        layoutContext?.pin.dispatch?.(pinned ? { msg: 'clear_pin' } : { msg: 'set_pin', trackReference });
    };

    let label = displayName;
    if (isScreenShare) label = t('tile.screenShare', { name: displayName });
    else if (participant.isLocal) label = t('tile.localName', { name: displayName });

    return (
        <div
            className={classes.tile}
            data-hand-raised={handRaised}
            data-source={source}
            data-speaking={isSpeaking && !isScreenShare}
        >
            {isTrackReference(trackReference) && !videoMuted ? (
                <VideoTrack
                    className={classes.video}
                    manageSubscription={autoManageSubscription}
                    trackRef={trackReference}
                    onSubscriptionStatusChanged={clearPinOnUnsubscribe}
                />
            ) : (
                <UserAvatar
                    classNames={{ root: classes.avatar, placeholder: classes.avatarInitials }}
                    name={displayName}
                    style={{ '--avatar-size': 'min(56cqh, 56cqw, calc(var(--spacing-72) + var(--spacing-3)))' }}
                />
            )}
            {(showHand || showLocalRecording) && (
                <Group className={classes.hand} gap="var(--spacing-1)" wrap="nowrap">
                    {showHand && (
                        <Tooltip label={t('tile.handRaised')}>
                            <ThemeIcon
                                aria-label={t('tile.handRaised')}
                                color="success"
                                radius="var(--radius-rounded-full)"
                                size="var(--spacing-7)"
                            >
                                <IconHandStop size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    )}
                    {showLocalRecording && (
                        <Tooltip
                            label={
                                participant.isLocal ? t('recording.tileHintMine') : t('recording.tileHint', { name: displayName })
                            }
                        >
                            <Badge
                                color="red"
                                data-testid="tile-local-recording-badge"
                                leftSection={<RecordingDot tone="light" />}
                                variant="light"
                            >
                                {t('recording.tile')}
                            </Badge>
                        </Tooltip>
                    )}
                </Group>
            )}
            {actions && layoutContext?.pin.dispatch && (
                <Tooltip label={pinned ? t('tile.unpin') : t('tile.pin')}>
                    <ActionIcon
                        aria-label={pinned ? t('tile.unpin') : t('tile.pin')}
                        aria-pressed={pinned}
                        className={classes.pin}
                        data-pinned={pinned}
                        radius="var(--radius-rounded-full)"
                        size="sm"
                        variant="default"
                        onClick={togglePin}
                    >
                        {pinned ? <IconPinnedOff size={16} /> : <IconPin size={16} />}
                    </ActionIcon>
                </Tooltip>
            )}
            {actions && !isScreenShare && <RemoveGuestMenu className={classes.menu} participant={participant} />}
            <div className={classes.nameRow}>
                <Group className={classes.nameChip} gap="var(--spacing-1)" wrap="nowrap">
                    {isScreenShare && <IconScreenShare size={12} />}
                    {!isScreenShare && micMuted && (
                        <IconMicrophoneOff aria-label={t('tile.micMuted')} color="var(--color-danger-default)" size={12} />
                    )}
                    <Text truncate fw={500} size="xs">
                        {label}
                    </Text>
                </Group>
            </div>
        </div>
    );
}
